/**
 * Core backend client. Replaces the legacy Hasura/GraphQL data layer: all data now
 * comes from the Core REST API under /kvk/** (cookie-based auth, credentials included).
 */

const BASE_URL = import.meta.env.REACT_APP_API_URL || 'https://api.islamstudies.nl'

export class CoreApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })
  if (!response.ok) {
    const text = await response.text().catch((): string => '')
    throw new CoreApiError(response.status, text || response.statusText)
  }
  if (response.status === 204) return undefined as T
  const raw = await response.text()
  return (raw ? JSON.parse(raw) : undefined) as T
}

// --- Types (mirror of the Core /kvk DTOs) ---

export enum KvkAchievementType {
  LESSON = 'LESSON',
  QUIZ = 'QUIZ',
  SOERAH = 'SOERAH',
  DICTIONARY = 'DICTIONARY',
  TRANSLATION = 'TRANSLATION',
}

export interface IKvkProfile {
  id: string
  username: string
  name: string | null
  avatar: string | null
  gender: string | null
  dateOfBirth: string | null
  openProfile: boolean
  stars: number
  createdAt: string
}

export interface IKvkEnrollment {
  id: string
  startDate: string
  endDate: string
  active: boolean
  /** A running subscription (Stripe, WooCommerce or Mollie) renews this enrollment. */
  subscribed: boolean
  selected: boolean
  profileLimit: number
  courseName: string
  profiles: IKvkProfile[]
}

export interface IKvkSubscriptionStatus {
  subscriptionId: string
  status: string
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  cancelAt: string | null
  pauseCollection: { behavior: string; resumesAt: string | null } | null
  enrollmentEndDate: string
  daysRemaining: number
  canPause: boolean
  canCancel: boolean
  canResume: boolean
  isPaused: boolean
}

export interface IKvkInvoice {
  id: string
  amountPaid: number
  currency: string
  status: string
  created: string
  invoicePdf: string | null
  hostedInvoiceUrl: string | null
  periodStart: string | null
  periodEnd: string | null
}

export interface IKvkWpSubscription {
  type: string
  status: string | null
  scheduledForCancellation: boolean
  canCancel: boolean
  canReactivate: boolean
  endDate: string | null
  nextPaymentDate: string | null
  amountCents?: number | null
}

export interface IKvkProfileDetails {
  aboutMe: string | null
  favoriteAnimal: string | null
  favoriteFood: string | null
  favoriteColor: string | null
  favoriteSport: string | null
  favoriteFilm: string | null
  favoriteBook: string | null
  favoriteSchoolSubject: string | null
  dreamJob: string | null
  hobbies: string | null
  hero: string | null
  favoriteSoerah: string | null
  favoriteLesson: string | null
  learnedAchievements: string | null
  thankfullFor: string | null
  wish: string | null
  finalMessage: string | null
}

export interface IKvkProfileFull {
  profile: IKvkProfile
  details: IKvkProfileDetails | null
}

export interface IKvkFile {
  id: string
  filename: string
  contentType: string | null
}

export interface IKvkFileUrl {
  fileId: string
  filename: string
  url: string
}

export interface IKvkAchievement {
  id: string
  title: string
  achievementType: string
  sequence: number | null
  accomplished: boolean
  accomplishedAt: string | null
}

export interface IKvkLessonProgress {
  progress: number
  timeInSeconds: number
  completed: boolean
}

export interface IKvkLessonOverview {
  id: string
  name: string
  description: string | null
  sequence: number
  label: string | null
  lessonNumber: string | null
  image: string | null
  quiz: string | null
  locked: boolean
  vimeoId: number | null
  progress: IKvkLessonProgress | null
  achievements: IKvkAchievement[]
}

export interface IKvkLessonVerse {
  id: string
  verse: string | null
  pronunciation: string | null
  translation: string | null
  sequence: number
  duration: number | null
  fragmentUrl: string | null
  fragment: IKvkFile | null
}

export interface IKvkLessonWord {
  id: string
  word: string
  meaning: string
  sequence: number | null
}

export interface IKvkLessonDetail {
  lesson: IKvkLessonOverview
  verses: IKvkLessonVerse[]
  words: IKvkLessonWord[]
}

export interface IKvkDictionaryWord {
  id: string
  character: string
  word: string
  meaning: string
}

export interface IKvkWeeklyFact {
  id: string
  message: string
  sequence: number | null
}

export interface IKvkRecitation {
  id: string
  title: string
  vimeoUrl: string | null
  image: string | null
  sequence: number
  dua: string | null
}

export interface IKvkPersonalDevelopment {
  id: string
  title: string
  subtitle: string | null
  vimeo: string | null
  image: string | null
  sequence: number
  attachment: IKvkFile | null
}

export interface IKvkLibraryDownload {
  id: string
  title: string
  files: IKvkFile[]
}

export interface IKvkFinalTestQuestion {
  id: string
  question: string
  sequence: number
  answer?: string
}

export interface IKvkFinalTestResult {
  finalTestQuestionId: string
  result: boolean
}

export interface IKvkFriendCard {
  profileId: string
  username: string
  name: string | null
  avatar: string | null
  stars: number
  joinedAt: string
}

export interface IKvkFriendRequest {
  id: string
  accepted: boolean | null
  createdAt: string
  sender: IKvkFriendCard
  receiver: IKvkFriendCard
}

export interface IKvkFriendRequestDetail extends IKvkFriendRequest {
  senderFavorites: IKvkProfileDetails | null
  receiverFavorites: IKvkProfileDetails | null
}

export interface IKvkFriendsOverview {
  sentPending: IKvkFriendRequest[]
  receivedPending: IKvkFriendRequest[]
  active: IKvkFriendRequest[]
}

export interface IKvkMessage {
  id: string
  title: string | null
  message: string | null
  senderType: string
  opened: boolean
  lessonId: string | null
  lessonName: string | null
  createdAt: string
}

export interface IKvkLessonMessage {
  lessonId: string
  lessonName: string | null
  title: string
  message: string
}

export interface IKvkMessagesOverview {
  messages: IKvkMessage[]
  lessonMessages: IKvkLessonMessage[]
}

export interface IKvkUnlockable {
  id: string
  type: 'FINAL_TEST' | 'FINAL_MESSAGE' | 'CERTIFICATE'
}

export interface IKvkNotification {
  id: string
  title: string
  description: string | null
  createdAt: string
}

export interface IKvkParentDetails {
  relationToKid: string | null
}

/** GET /user/me (Core UserDto). */
export interface ICoreUser {
  email: string
  defaultEnrollmentExists: boolean
  defaultEnrollmentExpired: boolean
}

// --- Auth ---

export const coreAuth = {
  me: (): Promise<ICoreUser> => request<ICoreUser>('/user/me'),
  requestMagicLink: (email: string): Promise<unknown> =>
    request('/auth/magic-link', { method: 'POST', body: JSON.stringify({ email }) }),
  verifyMagicLink: (token: string): Promise<unknown> =>
    request('/auth/magic-link/verify', { method: 'POST', body: JSON.stringify({ token }) }),
  loginWithPassword: (email: string, password: string): Promise<unknown> =>
    request('/auth/login/password', {
      method: 'POST',
      body: JSON.stringify({ username: email, password }),
    }),
  /** Mails a link to /wachtwoord-reset; Core picks this app from the request origin. */
  requestPasswordReset: (email: string): Promise<unknown> =>
    request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (email: string, token: string, newPassword: string): Promise<unknown> =>
    request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, token, newPassword }),
    }),
  logout: (): Promise<unknown> => request('/user/logout', { method: 'POST' }),
}

// --- KvK endpoints ---

export const kvkApi = {
  getEnrollment: (): Promise<IKvkEnrollment> => request<IKvkEnrollment>('/kvk/enrollment'),
  getEnrollments: (): Promise<IKvkEnrollment[]> => request<IKvkEnrollment[]>('/kvk/enrollment/all'),
  setActiveEnrollment: (enrollmentId: string): Promise<IKvkEnrollment> =>
    request<IKvkEnrollment>('/kvk/enrollment/active', {
      method: 'PUT',
      body: JSON.stringify({ enrollmentId }),
    }),

  getProfile: (profileId: string): Promise<IKvkProfileFull> => request<IKvkProfileFull>(`/kvk/profiles/${profileId}`),

  createProfile: (input: {
    username: string
    name: string
    dateOfBirth: string
    gender: string
    avatar: string
  }): Promise<IKvkProfileFull> =>
    request<IKvkProfileFull>('/kvk/profiles', { method: 'POST', body: JSON.stringify(input) }),

  updateProfile: (
    profileId: string,
    input: {
      username?: string
      name?: string
      avatar?: string
      gender?: string
      dateOfBirth?: string
      openProfile?: boolean
      details?: Partial<IKvkProfileDetails>
    }
  ): Promise<IKvkProfileFull> =>
    request<IKvkProfileFull>(`/kvk/profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  validatePincode: (pincode: number): Promise<{ valid: boolean }> =>
    request<{ valid: boolean }>('/kvk/pincode/validate', {
      method: 'POST',
      body: JSON.stringify({ pincode }),
    }),

  updatePincode: (pincode: number): Promise<void> =>
    request<void>('/kvk/pincode', { method: 'PUT', body: JSON.stringify({ pincode }) }),

  getLessons: (profileId: string): Promise<IKvkLessonOverview[]> =>
    request<IKvkLessonOverview[]>(`/kvk/profiles/${profileId}/lessons`),

  getLesson: (profileId: string, lessonId: string): Promise<IKvkLessonDetail> =>
    request<IKvkLessonDetail>(`/kvk/profiles/${profileId}/lessons/${lessonId}`),

  updateProgress: (
    profileId: string,
    lessonId: string,
    input: { progress: number; timeInSeconds: number; completed: boolean }
  ): Promise<IKvkLessonProgress> =>
    request<IKvkLessonProgress>(`/kvk/profiles/${profileId}/lessons/${lessonId}/progress`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  accomplishAchievements: (profileId: string, achievementIds: string[]): Promise<{ added: number; stars: number }> =>
    request<{ added: number; stars: number }>(`/kvk/profiles/${profileId}/achievements`, {
      method: 'POST',
      body: JSON.stringify({ achievementIds }),
    }),

  getDictionaryWords: (): Promise<IKvkDictionaryWord[]> => request<IKvkDictionaryWord[]>('/kvk/dictionary-words'),

  getWeeklyFacts: (): Promise<IKvkWeeklyFact[]> => request<IKvkWeeklyFact[]>('/kvk/weekly-facts'),

  getRecitations: (): Promise<IKvkRecitation[]> => request<IKvkRecitation[]>('/kvk/recitations'),

  getPersonalDevelopments: (): Promise<IKvkPersonalDevelopment[]> =>
    request<IKvkPersonalDevelopment[]>('/kvk/personal-developments'),

  getLibraryDownloads: (): Promise<IKvkLibraryDownload[]> => request<IKvkLibraryDownload[]>('/kvk/library-downloads'),

  getFinalTestQuestions: (includeAnswers = false): Promise<IKvkFinalTestQuestion[]> =>
    request<IKvkFinalTestQuestion[]>(`/kvk/final-test/questions?includeAnswers=${includeAnswers}`),

  getFinalTestResults: (profileId: string): Promise<IKvkFinalTestResult[]> =>
    request<IKvkFinalTestResult[]>(`/kvk/profiles/${profileId}/final-test/results`),

  upsertFinalTestResult: (profileId: string, input: IKvkFinalTestResult): Promise<IKvkFinalTestResult> =>
    request<IKvkFinalTestResult>(`/kvk/profiles/${profileId}/final-test/results`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  getFriends: (profileId: string): Promise<IKvkFriendsOverview> =>
    request<IKvkFriendsOverview>(`/kvk/profiles/${profileId}/friends`),

  getFriendCandidates: (profileId: string, search: string): Promise<IKvkFriendCard[]> =>
    request<IKvkFriendCard[]>(`/kvk/profiles/${profileId}/friends/candidates?search=${encodeURIComponent(search)}`),

  getFriendRequest: (profileId: string, requestId: string): Promise<IKvkFriendRequestDetail> =>
    request<IKvkFriendRequestDetail>(`/kvk/profiles/${profileId}/friends/${requestId}`),

  sendFriendRequest: (profileId: string, receiverProfileId: string): Promise<IKvkFriendRequest> =>
    request<IKvkFriendRequest>(`/kvk/profiles/${profileId}/friends`, {
      method: 'POST',
      body: JSON.stringify({ receiverProfileId }),
    }),

  acceptFriendRequest: (profileId: string, requestId: string): Promise<IKvkFriendRequest> =>
    request<IKvkFriendRequest>(`/kvk/profiles/${profileId}/friends/${requestId}/accept`, {
      method: 'PUT',
    }),

  deleteFriendRequest: (profileId: string, requestId: string): Promise<void> =>
    request<void>(`/kvk/profiles/${profileId}/friends/${requestId}`, { method: 'DELETE' }),

  getMessages: (profileId: string): Promise<IKvkMessagesOverview> =>
    request<IKvkMessagesOverview>(`/kvk/profiles/${profileId}/messages`),

  sendMessage: (
    profileId: string,
    input: { title?: string; message: string; senderType: string }
  ): Promise<IKvkMessage> =>
    request<IKvkMessage>(`/kvk/profiles/${profileId}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  markMessageRead: (profileId: string, messageId: string): Promise<IKvkMessage> =>
    request<IKvkMessage>(`/kvk/profiles/${profileId}/messages/${messageId}/read`, {
      method: 'PUT',
    }),

  markLessonMessageRead: (profileId: string, lessonId: string): Promise<IKvkMessage> =>
    request<IKvkMessage>(`/kvk/profiles/${profileId}/messages/lesson/${lessonId}/read`, {
      method: 'POST',
    }),

  getUnlockables: (profileId: string): Promise<IKvkUnlockable[]> =>
    request<IKvkUnlockable[]>(`/kvk/profiles/${profileId}/unlockables`),

  unlock: (profileId: string, type: string): Promise<IKvkUnlockable> =>
    request<IKvkUnlockable>(`/kvk/profiles/${profileId}/unlockables`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    }),

  lock: (profileId: string, type: string): Promise<void> =>
    request<void>(`/kvk/profiles/${profileId}/unlockables/${type}`, { method: 'DELETE' }),

  getNotifications: (): Promise<IKvkNotification[]> => request<IKvkNotification[]>('/kvk/notifications'),

  getParentDetails: (): Promise<IKvkParentDetails> => request<IKvkParentDetails>('/kvk/parent'),

  updateParentDetails: (relationToKid: string): Promise<IKvkParentDetails> =>
    request<IKvkParentDetails>('/kvk/parent', {
      method: 'PUT',
      body: JSON.stringify({ relationToKid }),
    }),

  getFileUrls: (fileIds: string[]): Promise<IKvkFileUrl[]> =>
    request<IKvkFileUrl[]>('/kvk/files/urls', {
      method: 'POST',
      body: JSON.stringify({ fileIds }),
    }),

  // Stripe study renewal (shared backend endpoint; response is wrapped in the
  // standard CustomApiResponse envelope, so we unwrap .data here).
  createCheckoutSession: (enrollmentId: string): Promise<ICheckoutSession> =>
    request<{ data: ICheckoutSession }>('/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ enrollmentId }),
    }).then((response) => response.data),

  // Subscription management (shared /stripe + /wp endpoints, CustomApiResponse-wrapped).
  getStripeSubscriptionStatus: (enrollmentId: string): Promise<IKvkSubscriptionStatus> =>
    request<{ data: IKvkSubscriptionStatus }>(`/stripe/subscription-status?enrollmentId=${enrollmentId}`).then(
      (response) => response.data
    ),

  getStripeInvoices: (enrollmentId: string): Promise<IKvkInvoice[]> =>
    request<{ data: { invoices: IKvkInvoice[] } }>(`/stripe/invoices?enrollmentId=${enrollmentId}&limit=10`).then(
      (response) => response.data.invoices ?? []
    ),

  pauseSubscription: (enrollmentId: string, resumeDate: string): Promise<void> =>
    request<{ data: unknown }>('/stripe/pause-subscription', {
      method: 'POST',
      body: JSON.stringify({ enrollmentId, resumeDate }),
    }).then((): void => undefined),

  resumeSubscription: (enrollmentId: string): Promise<void> =>
    request<{ data: unknown }>('/stripe/resume-subscription', {
      method: 'POST',
      body: JSON.stringify({ enrollmentId }),
    }).then((): void => undefined),

  createBillingPortalSession: (enrollmentId: string, returnUrl: string): Promise<string> =>
    request<{ data: { url: string } }>('/stripe/create-billing-portal-session', {
      method: 'POST',
      body: JSON.stringify({ enrollmentId, returnUrl }),
    }).then((response) => response.data.url),

  getWpSubscriptionStatus: (enrollmentId: string): Promise<IKvkWpSubscription> =>
    request<{ data: IKvkWpSubscription }>(`/wp/subscription-status?enrollmentId=${enrollmentId}`).then(
      (response) => response.data
    ),
}

export interface ICheckoutSession {
  sessionId: string
  url: string
}

/** Cross-component refresh signal after friend mutations (replaces Apollo refetchQueries). */
export const FRIENDS_REFRESH_EVENT = 'kvk:friends-refresh'

export const dispatchFriendsRefresh = (): void => {
  window.dispatchEvent(new Event(FRIENDS_REFRESH_EVENT))
}
